import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { isBrowser } from '../isBrowser';

const INITIAL_RETRY_MS = 100;
const MAX_RETRY_MS = 5000;
const BACKOFF_FACTOR = 2;

type KaiProApiStateType = {
  isOffline: boolean;
  apiUrl?: string;
  /** Device pixels per layout pixel. Sizes photo requests. */
  displayPixelRatio: number;
};

/** Used outside a browser until the platform reports its own ratio. 2x suits most phones. */
export const DEFAULT_DISPLAY_PIXEL_RATIO = 2;

const getInitialDisplayPixelRatio = () => isBrowser && window.devicePixelRatio ? window.devicePixelRatio : DEFAULT_DISPLAY_PIXEL_RATIO;

const KaiProApiStateContext = createContext<KaiProApiStateType | undefined>(undefined);

const getRetryDelay = (attempt: number) => {
  const delayMs = INITIAL_RETRY_MS * Math.pow(BACKOFF_FACTOR, attempt - 1);
  return Math.min(delayMs, MAX_RETRY_MS) | 0;
};

export type KaiProApiStateProviderInit = (setIsOffline: (isOffline: boolean) => void) => Promise<{
  isOffline: boolean;
  apiUrl?: string;
  /** The device's pixel ratio. Browsers are detected automatically; other platforms default to DEFAULT_DISPLAY_PIXEL_RATIO. */
  displayPixelRatio?: number;
  cleanup?: () => void;
}>;

type KaiProApiStateProviderProps = {
  children: ReactNode;
  init: KaiProApiStateProviderInit;
};

const getHeartbeatIsOffline = async (apiUrl: string) => {
  try {
    await fetch(`${apiUrl}/api/heartbeat`, { method: 'GET', cache: 'no-store' });
    return false;
  } catch (error) {
    return true;
  }
};

export const KaiProApiStateProvider = ({ children, init }: KaiProApiStateProviderProps) => {
  const [systemNetworkIsOffline, _setSystemNetworkIsOffline] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [apiUrl, setApiUrl] = useState<string>('');
  const [displayPixelRatio, setDisplayPixelRatio] = useState(getInitialDisplayPixelRatio);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  const hasComeOnlineOnceRef = useRef(false);
  const systemNetworkIsOfflineRef = useRef(true);
  systemNetworkIsOfflineRef.current = systemNetworkIsOffline;

  // Init: wire up platform connectivity detection
  useEffect(() => {
    const doInit = async () => {
      const setSystemNetworkIsOffline = (systemNetworkIsOffline: boolean) => {
        console.log('setSystemNetworkIsOffline', systemNetworkIsOffline);
        if (systemNetworkIsOffline === systemNetworkIsOfflineRef.current) return;
        systemNetworkIsOfflineRef.current = systemNetworkIsOffline;
        if (systemNetworkIsOffline) setIsOffline(true);
        _setSystemNetworkIsOffline(systemNetworkIsOffline);
      };

      const { isOffline, apiUrl, displayPixelRatio, cleanup } = await init(setSystemNetworkIsOffline);
      setApiUrl(apiUrl ?? '');
      if (displayPixelRatio) setDisplayPixelRatio(displayPixelRatio);
      _setSystemNetworkIsOffline(isOffline);
      cleanupRef.current = cleanup;
    };
    doInit();
    return () => {
      cleanupRef.current?.();
    };
  }, [init]);

  // Heartbeat verification: when returning online after being offline, verify
  // API is reachable. On initial startup we trust the OS optimistically and
  // skip the heartbeat so the app can start working immediately.
  useEffect(() => {
    if (!systemNetworkIsOffline && !hasComeOnlineOnceRef.current) {
      // fast-pass the first time we come online; The app starts out 'offline' and instantly becomes 'online' under normal conditions.
      // Rather than delay loading for confirmation, we assume the platform is online and start working immediately.
      hasComeOnlineOnceRef.current = true;

      console.log('KaiProApiStateProvider isOffline useEffect: setting isOffline to false (one-time fast-pass)');
      setIsOffline(false);
      return;
    }
    console.log('KaiProApiStateProvider isOffline useEffect: systemNetworkIsOffline', systemNetworkIsOffline);

    let destroyed = false;
    let heartbeatRetryTimeout: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;

    const checkHeartbeat = async () => {
      if (destroyed) return;
      if (systemNetworkIsOfflineRef.current) {
        console.log(
          'KaiProApiStateProvider isOffline useEffect: checkHeartbeat: systemNetworkIsOffline is true -> setting isOffline to true and stopping retries',
        );
        setIsOffline(true);
        return;
      }
      const heartbeatIsOffline = await getHeartbeatIsOffline(apiUrl);
      if (destroyed) return;
      console.log(
        'KaiProApiStateProvider isOffline useEffect: checkHeartbeat: heartbeatIsOffline',
        heartbeatIsOffline,
        '-> setting isOffline to',
        heartbeatIsOffline,
      );
      setIsOffline(heartbeatIsOffline);
      if (heartbeatIsOffline) {
        console.log(
          'KaiProApiStateProvider isOffline useEffect: checkHeartbeat: heartbeatIsOffline is true -> setting heartbeatRetryTimeout to',
          getRetryDelay(++retryCount),
        );
        heartbeatRetryTimeout = setTimeout(checkHeartbeat, getRetryDelay(++retryCount));
      }
    };

    checkHeartbeat();

    return () => {
      destroyed = true;
      clearTimeout(heartbeatRetryTimeout);
    };
  }, [systemNetworkIsOffline, apiUrl]);

  return (
    <KaiProApiStateContext.Provider value={{ isOffline, apiUrl, displayPixelRatio }}>
      {children}
    </KaiProApiStateContext.Provider>
  );
};

export const useKaiProApiState = (): KaiProApiStateType => {
  const context = useContext(KaiProApiStateContext);
  if (!context) {
    throw new Error('useKaiProApiState must be used within an KaiProApiStateProvider');
  }
  return context;
};
